import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import Select, {
  components,
  OptionProps,
} from 'react-select'
import {
  Accordion,
  Button,
  Card,
  CardGroup,
  Col,
  Form,
  OverlayTrigger,
  Row,
  Spinner,
  Tab,
  Table,
  Tabs,
  Tooltip,
  useAccordionButton,
} from 'react-bootstrap'
import { NavLink } from 'react-router-dom'
import { CSVLink } from 'react-csv'
import {
  BsBarChartLine,
  BsTable,
} from 'react-icons/bs'
import {
  format,
  subDays,
} from 'date-fns'

import Device, { DeviceIDTypes } from '@data/device/model/device'
import { useDevicesWaterUsage } from '@data/waterUsage/waterUsage'
import {
  calculateCo2Produced,
  convertCost,
  convertToUnitOfVolume,
  filterDevicesDueTo30DaysReport,
  formatCurrency,
  roundToTwo,
} from '@common/utils/helperFunctions'
import {
  useActiveAlertsQuery,
  useAlertStatsQuery,
  useHistoricalAlertsQuery,
} from '@data/alerts/alerts'
import { useAuthStore } from '@store/auth.store'
import DateRangeComponent from '../Calendar/DateRangeSelector'
import { Bar } from 'react-chartjs-2'
import { CheckCircle2 } from 'lucide-react'
import {
  getChartOptions,
  getMultipleDevicesDailyDatasets,
} from '../../WaterUsage/helper-functions'
import { COLUMNS } from '../../WaterUsage/constants'
import jsPDF from 'jspdf'
import { Chart as ChartJS } from 'chart.js'
import { buildPDFReport } from './helper-functions'
import {
  IReportTotalProps,
  IUsageDay,
  IWaterUsageProps,
} from './interfaces'
import { DEVICE_ACCESS_DENIED_MESSAGE } from '@components/general/DeviceAccessNotice/constants'
import {
  useDeviceAccessDenied,
  useDeviceDataUpdate,
  useSelectedDevices,
} from '@data/device/device'
import { useDeviceStore } from '@store/device.store'
import { useSelectedAccounts } from '@data/accounts/accounts'
import DeviceAccessNotice from '@components/general/DeviceAccessNotice/DeviceAccessNotice'
import ESGEmailModal from './ESGEmailModal'
import { useCurrentUserQuery } from '@data/currentUser/currentUser'
import {
  DateTypes,
  deviceWaterUsage,
  WaterUsageQueryMultipleDevices,
} from '@data/waterUsage/model/waterUsage.model'
import { UOM_OPTIONS } from '../../DeviceManagement/DeviceInformationManager/constants'

import 'react-datepicker/dist/react-datepicker.css'
import './ESG.scss'
import { hasPermissions } from '@common/utils/validation/authValidators'


const MAX_SELECTABLE_DEVICES = 50

/*
  0: Not asked
  1: Asked, not ready
  2: Ready
*/
// type GenerateReportState = 0 | 1 | 2;

const ESGReportingComponent: React.FC<IWaterUsageProps> = ({ devices }: IWaterUsageProps) => {
  const NOW = new Date()
  const BACK_30_DAYS = subDays(NOW, 30)

  const permissions = useAuthStore((state) => state.permissions)
  const selectedDevices = useSelectedDevices()
  const accessDenied = useDeviceAccessDenied()
  const selectedAccounts = useSelectedAccounts()
  const { data: userInfo } = useCurrentUserQuery()

  const setSelectedDeviceIds = useDeviceStore((state) => state.setSelectedDeviceIds)
  const { mutateAsync: updateDeviceData } = useDeviceDataUpdate()
  const { data: queue = [] } = useActiveAlertsQuery()
  const {
    data: historical = [],
    isFetching: isFetchingHistory,
    isLoading: isLoadingHistory,
  } = useHistoricalAlertsQuery({ ackResult: [1, 2, 3] })

  const [csvData, setCsvData] = useState<(string | number)[][]>([])
  const [fileName, setFileName] = useState('esg_report')
  const [dailyDatasets, setDailyDatasets] = useState<any>()
  const [activeAlerts, setActiveAlerts] = useState<number>(0)
  const [generateReportState, setGenerateReportState] = useState(0)
  const [totalUsage, setTotalUsage] = useState<null | number>(null)
  const [totalUsageVolume, setTotalUsageVolume] = useState<null | string>(null)
  const [fromDate, setFromDate] = useState<Date | null>(BACK_30_DAYS)
  const [toDate, setToDate] = useState<Date | null>(NOW)
  const [didSomethingChange, setDidSomethingChange] = useState(true)
  const [alertStatsAllDevice, setAlertStatsAllDevice] = useState<number>(0)
  const [selectedDevicesIds, setSelectedDevicesIds] = useState<string[]>([])
  const [alertStatsPeriodDevice, setAlertStatsPeriodDevice] = useState<number>(0)
  const [earliestInstallEpoch, setEarliestInstallEpoch] = useState<number | null>(null)
  const [totalUsageByCurrency, setTotalUsageByCurrency] = useState<
    Record<string, { totalUsage: number; totalCost: number; devices: number }>
  >({})

  const [displaySendEmailModal, setDisplaySendEmailModal] = useState(false)
  const [pdfBase64, setPdfBase64] = useState('')
  const [selectedUom, setSelectedUom] = useState(userInfo?.preferences?.uom || 'Liters')

  const dUuidSet = useMemo(
    () => new Set(devices.map((d) => d.dUuid)),
    [devices],
  )

  const filteredQueue = useMemo(
    () => queue.filter((q) => dUuidSet.has(q.dUuid)),
    [queue, dUuidSet],
  )

  const alertsShowInProgress = hasPermissions(permissions, ['READ:ALERTS:USAGE:IN_PROGRESS'])

  const tableRef = useRef(null)
  const chartRef = useRef<ChartJS<'bar', number[], string>>(null)

  const devicesDueTo30DaysReport = filterDevicesDueTo30DaysReport(devices)

  const co2Total = totalUsage
    ? calculateCo2Produced(selectedUom, totalUsage, selectedDevices)
    : null

  const queryPeriod: WaterUsageQueryMultipleDevices = {
    deviceIds: selectedDevicesIds,
    startDate: fromDate ? format(fromDate, 'yyyy-MM-dd\'T\'00:00:00xxx') : null,
    endDate: toDate ? format(toDate, 'yyyy-MM-dd\'T\'23:59:59xxx') : null,
    uom: selectedUom,
    idType: DeviceIDTypes.dUuid,
    dateType: DateTypes.timezone,
  }
  const {
    data: multipleDeviceUsage,
    isLoading: isLoadingUsage,
    isFetching: isFetchingUsage,
    refetch: refetchDevicesUsage,
  } = useDevicesWaterUsage(queryPeriod)
  const {
    data: alertStats = [],
    isFetching: isFetchingAlertStats,
    refetch: refetchAlertStats,
  } = useAlertStatsQuery(
    {
      deviceIds: queryPeriod.deviceIds,
      startDate: queryPeriod.startDate,
      endDate: queryPeriod.endDate,
      idType: queryPeriod.idType,
    },
    false,
  )
  const isLoading = isLoadingUsage || isLoadingHistory
  const isFetching = isFetchingUsage || isFetchingHistory || isFetchingAlertStats

  const csvHeaders: string[] = [
    'device_id',
    'device_name',
    'device_uuid',
    'start_date',
    'end_date',
    'usage',
    'usage_unit',
    'cost',
    'cost_unit',
    'co2',
    'co2_unit',
  ]

  useEffect(() => {
    setSelectedDeviceIds([])
  }, [selectedAccounts, devices])

  useEffect(() => {
    if (selectedDevices.length > 0 && multipleDeviceUsage) {
      const multipleDevicesDailyDatasets = getMultipleDevicesDailyDatasets(
        multipleDeviceUsage?.usage,
      )
      setDailyDatasets(multipleDevicesDailyDatasets)

      if (multipleDeviceUsage?.totalUsage) {
        const totalUsageConverted = convertToUnitOfVolume(
          selectedUom,
          multipleDeviceUsage.totalUsage,
        )

        setTotalUsage(multipleDeviceUsage.totalUsage)
        setTotalUsageVolume(totalUsageConverted)
      }

      if (multipleDeviceUsage?.totalUsageByDevice) {
        const totalUsageByCurrency = calculateTotalUsageByCurrency(
          selectedDevices,
          multipleDeviceUsage.totalUsageByDevice,
        )

        setTotalUsageByCurrency(totalUsageByCurrency)
      }
    }
  }, [multipleDeviceUsage])

  useEffect(() => {
    const statsPeriodTotal = alertStats.reduce((acc, stat) => {
      return acc + stat.alertCount
    }, 0)
    setAlertStatsPeriodDevice(statsPeriodTotal)
  }, [alertStats])

  useEffect(() => {
    setDidSomethingChange(true)
    setTotalUsage(null)

    if (selectedDevices.length > 0) {
      const selectedDeviceIds = selectedDevices.map((d) => d.dUuid)
      setSelectedDevicesIds(selectedDeviceIds)
      const selectedDUuids = new Set(selectedDeviceIds)
      const selectedDevicesAlerts = filteredQueue.filter((alert) =>
        selectedDUuids.has(alert.dUuid),
      )
      setActiveAlerts(selectedDevicesAlerts.length)

      let earliestDate = Infinity

      for (const item of selectedDevices) {
        const installDate = new Date(item.deviceSettings.installDate).getTime()
        if (installDate < earliestDate) {
          earliestDate = installDate
        }
      }

      setEarliestInstallEpoch(
        earliestDate !== Infinity ? earliestDate : null,
      )

      const from = fromDate ? format(fromDate, 'dd-MM-yyyy') : null
      const to = toDate ? format(toDate, 'dd-MM-yyyy') : null
      if (selectedDevices.length === 1) {
        setFileName(`${selectedDevices[0].deviceName}-${from}_${to}_esg_report`)
      } else {
        setFileName(`ESG_REPORT_multiple_devices-${from}_${to}`)
      }
    }
  }, [selectedDevices])

  useEffect(() => {
    setDidSomethingChange(true)
  }, [fromDate, toDate, selectedUom])

  const askForData = () => {
    if (selectedDevices.length > 0 && selectedDevices.length <= MAX_SELECTABLE_DEVICES) {
      void refetchDevicesUsage()
      void refetchAlertStats()
      sumUsageAlertsTotalForDeviceIds()
      setDidSomethingChange(false)
    }
  }

  const calculateTotalUsageByCurrency = (
    devices: Device[],
    totalUsage: Record<string, number>,
  ): Record<string, { totalUsage: number; totalCost: number; devices: number }> => {
    const totalUsageByCurrency: Record<
      string,
      { totalUsage: number; totalCost: number; devices: number }
    > = {}

    devices.forEach((device) => {
      const { deviceSettings, deviceId } = device
      const { currency, cost } = deviceSettings
      if (totalUsageByCurrency[currency]) {
        totalUsageByCurrency[currency].totalUsage += totalUsage[deviceId] || 0
        totalUsageByCurrency[currency].devices += 1
        totalUsageByCurrency[currency].totalCost += totalUsage[deviceId] * Number(cost) || 0
      } else {
        totalUsageByCurrency[currency] = {
          totalUsage: totalUsage[deviceId],
          devices: 1,
          totalCost: totalUsage[deviceId] * cost,
        }
      }
    })
    return totalUsageByCurrency
  }

  const sumUsageAlertsTotalForDeviceIds = () => {
    let totalAlerts = 0
    for (const deviceData of historical) {
      if (selectedDevicesIds.includes(deviceData.dUuid)) {
        totalAlerts += deviceData.tier1Total + deviceData.tier2Total + deviceData.tier3Total
      }
    }
    setAlertStatsAllDevice(totalAlerts)
  }

  function summarizeFlowData(usageData: deviceWaterUsage[][]): any {
    const flowSummary: any = {}

    usageData.forEach((obj) => {
      obj.forEach((entry: deviceWaterUsage) => {
        const { date, hour, flow } = entry
        const dateKey = date.split('T')[0]

        if (!flowSummary[dateKey]) {
          flowSummary[dateKey] = { date: dateKey, totalFlow: 0 } as IUsageDay
        }

        if (flow !== null && !isNaN(flow)) {
          flowSummary[dateKey].totalFlow += flow
          flowSummary[dateKey][`totalFlowHour${hour}`] =
            (flowSummary[dateKey][`totalFlowHour${hour}`] || 0) + flow
        } else {
          flowSummary[dateKey][`totalFlowHour${hour}`] = null
          // if (
          //   !isNaN(flowSummary[dateKey][`totalFlowHour${hour}`]) ||
          //   flowSummary[dateKey][`totalFlowHour${hour}`] !== null
          // ) {
          //   flowSummary[dateKey][`totalFlowHour${hour}`] =
          //     flowSummary[dateKey][`totalFlowHour${hour}`]
          // } else {
          //   flowSummary[dateKey][`totalFlowHour${hour}`] = null
          // }
        }
      })
    })

    return flowSummary
  }

  const getRows = (waterUsage: any) => {
    const groupedByDay = summarizeFlowData(waterUsage)
    return Object.values(groupedByDay).map((usage: any, idx: number) => {
      const renderHourCell = (hour: number) => {
        const value = usage[`totalFlowHour${hour}`]
        return value !== null && value !== undefined ? roundToTwo(value) : '-'
      }
      return (
        <tr key={idx}>
          <td style={{ position: 'sticky', left: 0 }}> {usage.date} </td>
          <td> {roundToTwo(usage.totalFlow)}</td>
          {[...Array(24).keys()].map((hour) => (
            <td key={`${idx}${hour}`}>{renderHourCell(hour)}</td>
          ))}
        </tr>
      )
    })
  }

  const getHeaders = () => {
    const periodColumns: Array<any> = [
      {
        name: 'Date',
        key: 'date',
        width: '120px',
      },
      {
        name: `Usage in ${selectedUom}`,
        key: 'usage',
        width: '120px',
      },
    ]

    const headersObj = [...periodColumns, ...COLUMNS]

    return headersObj.map((header: any) => (
      <th style={{ width: header.width ?? '80px' }} key={header.key}>
        {header.name}
      </th>
    ))
  }

  const generateCsvFile = () => {
    if (!selectedDevices.length) return
    const selectedDevicesData: (string | number)[][] = [[...csvHeaders]]
    for (const device of selectedDevices) {
      const convertedCost = convertCost(device.deviceSettings.conversionFactor, selectedUom, device.deviceSettings.cost)
      const deviceUsage = multipleDeviceUsage?.totalUsageByDevice[device.deviceId]
      selectedDevicesData.push([
        device.deviceId,
        device.deviceName,
        device.dUuid,
        fromDate ? format(fromDate, 'yyyy-MM-dd\'T\'00:00') : '',
        toDate ? format(toDate, 'yyyy-MM-dd\'T\'00:00') : '',
        deviceUsage || '',
        selectedUom,
        deviceUsage ? roundToTwo((convertedCost * deviceUsage) / 1000) : '',
        device.deviceSettings.currency,
        deviceUsage ? calculateCo2Produced(selectedUom, deviceUsage, devices).value : '',
        'Kg',
      ])
    }
    setCsvData(selectedDevicesData)
  }

  const downloadPdfDocument = (sendReportByEmail = false) => {
    if (selectedDevices.length) {
      const tableHTML = tableRef.current
      if (!chartRef) return
      if (!chartRef.current) return
      if (!tableHTML) return
      const chartImage = chartRef.current.toBase64Image()

      const totalCostByCurrency = Object.entries(totalUsageByCurrency).map(([currency, data]) => {
        return `${formatCurrency(
          roundToTwo(roundToTwo(data.totalCost / 1000)),
          currency,
        )} ${selectedDevices.length > 1 ? `(${data.devices} devices)` : ''}`
      })

      const reportTotals: IReportTotalProps = {
        usageTotal: totalUsage ? totalUsage : null,
        totalVolume: totalUsageVolume && totalUsageVolume,
        co2Total: `${co2Total?.value + ' ' + co2Total?.unit}`,
        activeAlerts: activeAlerts,
        periodAlerts: alertStatsPeriodDevice,
        totalAlerts: alertStatsAllDevice,
        totalCostByCurrency,
      }
      buildPDFReport(
        selectedDevices,
        fromDate,
        toDate,
        chartImage,
        tableHTML,
        reportTotals,
        selectedUom,
      ).then((pdf: jsPDF) => {
        if (!sendReportByEmail) {
          setGenerateReportState(2)
          pdf.save(fileName)
        } else {
          generateCsvFile()
          setPdfBase64(pdf.output('datauristring'))
          setDisplaySendEmailModal(true)
        }
      })
    }
  }

  const ValueContainer = (containerProps: any) => {
    if (isLoading || isFetching) {
      return <> Loading...</>
    }

    if (selectedAccounts.length === 0) {
      return <span className="ml-3">Please select an account first...</span>
    }

    return (
      <components.ValueContainer {...containerProps} className={'d-flex'}>
        Devices ({selectedDevices.length} / {devices.length}){containerProps.children}
      </components.ValueContainer>
    )
  }

  const Option = (commonProps: OptionProps) => {
    return (
      <components.Option {...commonProps}>
        <Form.Check
          checked={commonProps.isSelected}
          readOnly
          label={commonProps.children}
        />
      </components.Option>
    )
  }


  const optionStyles = (styles: any, { isFocused, isDisabled }: any) => {
    return {
      ...styles,
      backgroundColor: isFocused ? '#eee' : null,
      color: isDisabled ? '#dedede' : '#333',
      cursor: 'pointer',
    }
  }

  const MenuList = (props: any) => {
    return (
      <components.MenuList {...props}>
        {devices.length >= MAX_SELECTABLE_DEVICES ? (
          <div className="ml-3 mt-2 mb-2">
            **{' '}
            <span className="font-weight-bold text-decoration-underline">
              {`You can select up to ${MAX_SELECTABLE_DEVICES} devices`}
            </span>{' '}
            **
          </div>
        ) : selectedDevices.length > 0 ? (
          <button className="btn btn-link" onClick={() => setSelectedDeviceIds([])}>
            deselect all
          </button>
        ) : (
          <button className="btn btn-link" onClick={() => setSelectedDeviceIds(devices.map((device) => device.deviceId))}>
            select all
          </button>
        )}

        {props.children}
      </components.MenuList>
    )
  }
  const CustomToggle = ({ children, eventKey, callback }: any) => {
    const decoratedOnClick = useAccordionButton(eventKey, () => callback && callback(eventKey))

    return <Button onClick={decoratedOnClick}>{children}</Button>
  }

  const handleCompleteReport = async (device: Device) => {
    const updatedDevice = { ...device }
    updatedDevice.deviceSettings.report30Day = true
    if (device.dUuid) {
      await updateDeviceData({
        deviceId: device?.dUuid,
        idType: DeviceIDTypes.dUuid,
        device: updatedDevice,
      })
    }
  }

  return (
    <div className="esg-report">
      <Row className="main-content">
        <Col sm={6}>
          <h1>ESG Summary</h1>
          <Row>
            <div className="section-header">
              <div className="">Device:</div>
            </div>
            <Select
              styles={{ option: optionStyles }}
              components={{ ValueContainer, MenuList, Option }}
              className="basic-multi-select"
              value={selectedDevices}
              onChange={(options: any) =>
                setSelectedDeviceIds([...options].map((device: Device) => device.deviceId))
              }
              options={devices}
              isMulti
              isOptionDisabled={() => selectedDevices.length === MAX_SELECTABLE_DEVICES}
              name="devices"
              classNamePrefix="select"
              getOptionValue={(option: any) => option.deviceId}
              getOptionLabel={(option: any) => `${option.deviceName}`}
              isClearable={false}
              hideSelectedOptions={false}
              closeMenuOnSelect={false}
              isLoading={!accessDenied && (isLoading || devices.length === 0)}
              isDisabled={isLoading || devices.length === 0}
              noOptionsMessage={() =>
                accessDenied ? DEVICE_ACCESS_DENIED_MESSAGE : 'No devices'
              }
            />
            <DeviceAccessNotice />
          </Row>
          <Row>
            <DateRangeComponent
              from={BACK_30_DAYS}
              to={NOW}
              fromCallback={setFromDate}
              toCallback={setToDate}
              minDate={
                earliestInstallEpoch
                  ? new Date(earliestInstallEpoch)
                  : undefined
              }
            />
          </Row>
          <Row className="mb-3 w-50">
            <Form.Group as={Col} controlId="expected-percentage-savings">
              <Form.Label>Unit of measure</Form.Label>
              <Select
                classNamePrefix="select"
                value={{
                  value: selectedUom,
                  label: selectedUom,
                }}
                onChange={(e: any) => setSelectedUom(e.value)}
                options={UOM_OPTIONS.map((uom) => {
                  return {
                    value: uom,
                    label: uom === userInfo?.preferences?.uom ? `${uom} (your set preference)` : uom,
                  }
                })}
                getOptionValue={(option) => option.value}
                getOptionLabel={(option) => `${option.label}`}
                isClearable={false}
              />
            </Form.Group>
          </Row>
          <div className="get-data-button">
            <Button
              variant="primary"
              onClick={askForData}
              disabled={selectedDevices.length === 0 || isFetching || isLoading || selectedDevices.length > MAX_SELECTABLE_DEVICES || !fromDate || !toDate}
            >
              {isLoading || isFetching ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                  />{' '}
                  <span>Getting Data...</span>
                </>
              ) : (
                <>Get Data</>
              )}
            </Button>
          </div>
        </Col>
        {hasPermissions(permissions, ['ACCOUNT:ADMIN:SMARTFLOW']) &&
        devicesDueTo30DaysReport.length > 0 ? (
          <Col sm={6} className="devicesDue d-flex flex-column align-items-center">
            <div className="h4 mt-3">
              <strong>
                {`${devicesDueTo30DaysReport.length} devices are due to a 30 day report`}
              </strong>
            </div>
            <ul>
              {devicesDueTo30DaysReport.map((device) => (
                <div className="d-flex align-items-top-center" key={device.deviceId}>
                  <li>{`${device.deviceName} (${format(
                    device.deviceSettings.monitoringStartDate, 'dd-MM-yyyy')} monitoring start date)`}</li>
                  <OverlayTrigger
                    delay={{ hide: 450, show: 300 }}
                    overlay={(props: any) => <Tooltip {...props}>Mark as completed</Tooltip>}
                    placement="bottom"
                  >
                    <CheckCircle2
                      role="button"
                      size={20}
                      color={'green'}
                      className="ml-2 mt-1"
                      onClick={() => handleCompleteReport(device)}
                    />
                  </OverlayTrigger>
                </div>
              ))}
            </ul>
          </Col>
        ) : (
          ''
        )}
      </Row>
      <div className="data-container">
        {isLoading || isFetching ? (
          <div>Loading...</div>
        ) : (
          totalUsage &&
          !isLoading &&
          !isFetching &&
          !didSomethingChange && (
            <Col className="data">
              <Row>
                <Col>
                  <Row>
                    <CardGroup
                      className="gap-3"
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, 12.5rem)',
                      }}
                    >
                      {selectedDevices.length > 0 &&
                        selectedDevices.map((device) => {
                          return (
                            <Card className="shadow" key={device.deviceId}>
                              <Card.Body>
                                {device?.deviceName ? <h4>{device?.deviceName}</h4> : null}
                              </Card.Body>
                            </Card>
                          )
                        })}
                    </CardGroup>
                  </Row>
                </Col>
              </Row>
              <Row>
                <Col lg={10}>
                  <Row>
                    <Col lg={4}>
                      <Card className="shadow">
                        <Card.Body>
                          <h4>
                            <i className="fas fa-fw fa-dollar-sign mr-3"></i>Total Cost
                          </h4>
                          <div>
                            {Object.entries(totalUsageByCurrency).map(([currency, data]) => {
                              return (
                                <b className="ml-5 d-flex" key={currency}>
                                  {formatCurrency(
                                    roundToTwo(roundToTwo(data.totalCost / 1000)),
                                    currency,
                                  )}
                                  {`${
                                    selectedDevices.length > 1 ? `(${data.devices} devices)` : ''
                                  }`}
                                </b>
                              )
                            })}
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col lg={4}>
                      <Card className="shadow">
                        <Card.Body>
                          <h4>
                            <i className={'fas fa-fw fa-water mr-3'}></i>Total Water Consumed
                          </h4>
                          <b>
                            <span className="ml-5">{totalUsageVolume}</span>
                          </b>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col lg={4}>
                      <Card className="shadow">
                        <Card.Body>
                          <h4>
                            <i className="fas fa-fw fa-cloud mr-3"></i>Total CO<sub>2</sub> Produced
                          </h4>
                          <b>
                            <span className="ml-5">
                              {`${co2Total?.value + ' ' + co2Total?.unit}`}
                            </span>
                          </b>
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>
                  <Row>
                    <Col lg={4}>
                      <Card className="shadow">
                        <Card.Body>
                          <h4>
                            <i className="fas fa-fw fa-exclamation-triangle mr-3"></i>Active Usage
                            Alerts
                          </h4>
                          <b>
                            <span className="ml-5">{activeAlerts}</span>
                          </b>
                          {alertsShowInProgress && activeAlerts > 0 ? (
                            <NavLink to="/alert-queue">view</NavLink>
                          ) : null}
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col lg={4}>
                      <Card className="shadow">
                        <Card.Body>
                          <h4>
                            <i className="fas fa-fw fa-exclamation-triangle mr-3"></i>Usage Alerts
                          </h4>
                          <b>
                            <span className="ml-5">{alertStatsPeriodDevice}</span>
                          </b>{' '}
                          ({fromDate ? format(fromDate, 'dd/MM/yyyy') : null} to{' '}
                          {toDate ? format(toDate, 'dd/MM/yyyy') : null})
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col lg={4}>
                      <Card className="shadow">
                        <Card.Body>
                          <h4>
                            <i className="fas fa-fw fa-exclamation-triangle mr-3"></i>Usage Alerts
                            Total
                          </h4>
                          <b>
                            <span className="ml-5">{alertStatsAllDevice}</span>
                          </b>
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>
                </Col>
              </Row>
              <Row>
                <Col>
                  <div className={'my-butts'}>
                    <CSVLink
                      data={csvData}
                      onClick={generateCsvFile}
                      className="btn btn-primary mr-lg-2"
                      filename={`${fileName}.csv`}
                    >
                      Generate CSV Report
                    </CSVLink>
                    <Button onClick={() => downloadPdfDocument()}>
                      <div>
                        {generateReportState === 1 ? (
                          <>
                            <Spinner
                              as="span"
                              animation="border"
                              size="sm"
                              role="status"
                              aria-hidden="true"
                            />
                            <span>Generating...</span>
                          </>
                        ) : (
                          <>Generate PDF Report</>
                        )}
                      </div>
                    </Button>
                    {selectedDevices.length < 2 && (
                      <Button className="ml-3" onClick={() => downloadPdfDocument(true)}>
                        Send Reports by email
                      </Button>
                    )}
                  </div>
                </Col>
              </Row>
              <Row>
                <Col>
                  <Accordion defaultActiveKey="0">
                    <Card>
                      <Card.Header>
                        <CustomToggle eventKey="0">
                          <span className="fas fa-hand-holding-water fa-lg mr-2"></span> Show/Hide
                          Usage
                        </CustomToggle>
                      </Card.Header>
                      <Accordion.Collapse eventKey="0">
                        <Card.Body>
                          <div className="data-visualizations">
                            <Tabs
                              transition={false}
                              id="controlled-tab-example"
                              // activeKey={selectedPeriod}
                              // onSelect={(k: any) => setSelectedPeriod(k)}
                            >
                              <Tab eventKey={'usage'} title={<BsBarChartLine />}>
                                <article
                                  id="canvas-container-id-usage"
                                  className="canvas-container"
                                >
                                  <Bar
                                    width={2500}
                                    height={600}
                                    data={dailyDatasets}
                                    ref={chartRef}
                                    options={getChartOptions('daily', selectedUom)}
                                  />
                                </article>
                              </Tab>
                              <Tab eventKey={'table'} title={<BsTable />}>
                                <article
                                  id="canvas-container-id-table"
                                  className="canvas-container"
                                >
                                  <div className="table-container">
                                    <Table id="usage-table" ref={tableRef} responsive>
                                      <thead>
                                      <tr>{getHeaders()}</tr>
                                      </thead>
                                      <tbody>
                                      {multipleDeviceUsage?.usage &&
                                        getRows(multipleDeviceUsage.usage)}
                                      </tbody>
                                    </Table>
                                  </div>
                                </article>
                              </Tab>
                            </Tabs>
                          </div>
                        </Card.Body>
                      </Accordion.Collapse>
                    </Card>
                  </Accordion>
                </Col>
              </Row>
            </Col>
          )
        )}
      </div>

      {displaySendEmailModal && (
        <ESGEmailModal
          selectedDevice={selectedDevices[0]}
          onHide={() => setDisplaySendEmailModal(false)}
          pdfAttachment={pdfBase64}
          csvData={csvData}
          devicesDueTo30DaysReport={devicesDueTo30DaysReport}
        />
      )}
    </div>
  )
}

export default ESGReportingComponent
